import { useBoolean } from "src/hooks/use-boolean.ts";
import { useFormContext, useWatch } from "react-hook-form";
import { useList } from "src/system/useList.tsx";
import {
  PriceListItemCalculateType,
  type PriceListResource,
  type ProductPriceListItem,
} from "@exonn/js-sdk";
import { ResourceEnum } from "src/constants/constants.ts";
import { Accordion } from "@radix-ui/react-accordion";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { SelectPriceLists } from "src/admin/modals/product/SelectPriceLists/SelectPriceLists.tsx";
import type { FormValues } from "src/admin/forms/product/config.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { PriceListRow } from "src/components/Form/panels/PricePanel/components/product/PriceListRow/PriceListRow.tsx";

export const DefaultPriceListItems = () => {
  const {
    value: modalOpen,
    setFalse: closeModal,
    setTrue: openModal,
  } = useBoolean(false);
  const { t } = useTranslation(TranslationNamespaceEnum.PricePanel);
  const { control, getValues, setValue } = useFormContext<FormValues>();
  const { data, isLoading: isLoadingPriceLists } = useList<PriceListResource>({
    resource: ResourceEnum.PriceList,
    include: ["currency"],
    queryOptions: {
      refetchOnMount: true,
    },
  });
  const priceLists = data?.data || [];

  const priceListsItems = useWatch({ control, name: "priceListItems" });

  const assignedPriceLists = priceLists.filter((priceList) => {
    if (priceListsItems[priceList.id]) {
      return true;
    }
    return priceList.calculate_type !== null;
  });
  const handleSave = ({
    tempSelectedPriceListIds,
  }: {
    tempSelectedPriceListIds: string[];
  }) => {
    const { priceListItems } = getValues();

    const nextPriceListItems = priceLists.reduce(
      (acc, priceList) => {
        const isAutomaticPriceList = priceList.calculate_type !== null;
        const isSelected = tempSelectedPriceListIds.includes(priceList.id);
        const existingPriceListItem = priceListItems?.[priceList.id];

        if (isAutomaticPriceList && !isSelected) {
          return {
            ...acc,
            [priceList.id]: {
              calculate_type: PriceListItemCalculateType.Inactive,
              calculate_value: "0",
              calculate_from: "selling_price",
              is_calculate_round: false,
            },
          };
        }

        if (!isAutomaticPriceList && isSelected) {
          if (
            existingPriceListItem &&
            existingPriceListItem.calculate_type !==
              PriceListItemCalculateType.Inactive
          ) {
            return {
              ...acc,
              [priceList.id]: existingPriceListItem,
            };
          }
          return {
            ...acc,
            [priceList.id]: {
              calculate_type: PriceListItemCalculateType.Absolute,
              calculate_value: "0",
              calculate_from: "selling_price",
              is_calculate_round: false,
            },
          };
        }

        if (!isAutomaticPriceList && !isSelected && existingPriceListItem) {
          return {
            ...acc,
            [priceList.id]: {
              calculate_type: PriceListItemCalculateType.Inactive,
              calculate_value: "0",
              calculate_from: "selling_price",
              is_calculate_round: false,
            },
          };
        }

        return acc;
      },
      {} as { [id: string]: ProductPriceListItem },
    );

    setValue("priceListItems", nextPriceListItems, {
      shouldDirty: true,
    });
    closeModal();
  };
  const priceListItems = useWatch({
    name: `priceListItems`,
    control,
  });

  const selectedPriceListIds = priceLists
    .filter((priceList) => {
      if (priceListItems[priceList.id]) {
        return (
          priceListItems[priceList.id].calculate_type !==
          PriceListItemCalculateType.Inactive
        );
      }

      return priceList.calculate_type !== null;
    })
    .map((priceList) => priceList.id);

  return (
    <>
      {!!priceLists?.length && !!assignedPriceLists.length && (
        <Accordion type="multiple">
          {assignedPriceLists.map(({ id }) => {
            return <PriceListRow key={id} id={id} />;
          })}
        </Accordion>
      )}

      {!isLoadingPriceLists && (
        <ButtonV2
          css={{ alignSelf: "flex-start", marginTop: 5 }}
          size="sm"
          startAdornment={<i className="icon-15-price_lists" />}
          onClick={openModal}
        >
          {t("panel.add_button")}
        </ButtonV2>
      )}
      {modalOpen && (
        <SelectPriceLists
          selectedPriceListIds={selectedPriceListIds}
          priceLists={priceLists}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </>
  );
};
